import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import { LoadingSpinner, Table, TextLink, Tooltip } from '@/shared/components';
import { useLatestRef } from '@/shared/hooks/useLatestRef';
import { PermissionGuard } from '@/features/auth';
import { EyeIcon, PencilIcon, Trash2Icon } from 'lucide-react';
import { formatMoneyCurrency } from '@/shared/utils/formatMoneyDisplay';
import { ServiceForm } from './ServiceForm/ServiceForm';
import type { ServiceFormSubmitValues } from '../schemas/service.schema';
import type { PlanServiceRow } from '../types/plan.types';
import type { BackRef } from '../types/backRef';

interface Props {
    planId: string;
    data: PlanServiceRow[];
    isLoading?: boolean;
    // Resolves when persisted; a rejection keeps the expanded form open.
    onUpdate: (serviceId: string, values: ServiceFormSubmitValues) => Promise<unknown>;
    // Row, not id: the panel's confirm dialog names the service.
    onRemove: (row: PlanServiceRow) => void;
    isUpdating?: boolean;
    removingServiceId?: string | null;
    // Origin passed to ServiceDetail so its back breadcrumb returns here (the
    // plan) rather than falling back to the service's product.
    backRef?: BackRef;
}

export const PlanServiceDataTable: React.FC<Props> = ({
    planId,
    data,
    isLoading = false,
    onUpdate,
    onRemove,
    isUpdating = false,
    removingServiceId = null,
    backRef,
}) => {
    const navigate = useNavigate();
    const [editingId, setEditingId] = useState<string | null>(null);
    // Callers recreate these props on every render, so read them through refs rather
    // than listing them in the column memo deps, where any parent or query re-render
    // would rebuild the cells and remount an open editor.
    const onRemoveRef = useLatestRef(onRemove);
    const backRefRef = useLatestRef(backRef);

    const startEdit = useCallback((row: PlanServiceRow) => setEditingId(row.id), []);
    const cancelEdit = useCallback(() => setEditingId(null), []);

    const columns = useMemo<ColumnDef<PlanServiceRow>[]>(() => [
        {
            accessorKey: 'position',
            header: 'Position',
            cell: (info) => <span className="text-sm">{info.row.original.position}</span>,
        },
        {
            accessorKey: 'name',
            header: 'Name',
            cell: (info) => (
                <TextLink
                    color="primary" className="font-semibold text-left"
                    onClick={() => navigate(`/smr-admin/services/${info.row.original.id}`, { state: { backRef: backRefRef.current } })}
                >
                    {info.getValue() as string}
                </TextLink>
            ),
        },
        {
            accessorKey: 'service_type',
            header: 'Type',
            cell: (info) => <span className="text-sm">{info.row.original.service_type}</span>,
        },
        {
            id: 'total_net',
            header: 'Net Cost',
            cell: (info) => {
                const row = info.row.original;
                return <span className="text-sm">{formatMoneyCurrency(row.total_net.amount, row.total_net.currency)}</span>;
            },
        },
        {
            id: 'items_count',
            header: 'Items',
            cell: (info) => <span className="text-sm">{info.row.original.items_count}</span>,
        },
        {
            accessorKey: 'months_from_start',
            header: 'Months',
            cell: (info) => <span className="text-sm">{info.row.original.months_from_start}</span>,
        },
        {
            accessorKey: 'mileage_from_start',
            header: 'Miles',
            cell: (info) => <span className="text-sm">{info.row.original.mileage_from_start.toLocaleString('en-GB')}</span>,
        },
        {
            id: 'actions',
            header: 'Actions',
            enableSorting: false,
            cell: (info) => {
                const row = info.row.original;
                const isRemoving = row.id === removingServiceId;

                return (
                    <div className="flex space-x-2">
                        <Tooltip content="View service">
                            <TextLink
                                ariaLabel="View service"
                                color="primary"
                                onClick={() => navigate(`/smr-admin/services/${row.id}`, { state: { backRef: backRefRef.current } })}
                                startIcon={<EyeIcon size={20} />}
                                disabled={isRemoving}
                            />
                        </Tooltip>
                        <PermissionGuard permission={{ resource: 'smr', action: 'service.edit' }}>
                            <Tooltip content="Edit service">
                                <TextLink
                                    ariaLabel="Edit service"
                                    color="secondary"
                                    onClick={() => startEdit(row)}
                                    startIcon={<PencilIcon size={18} />}
                                    disabled={isRemoving || !!editingId}
                                />
                            </Tooltip>
                        </PermissionGuard>
                        <PermissionGuard permission={{ resource: 'smr', action: 'service.delete' }}>
                            <Tooltip content="Remove service">
                                <TextLink
                                    ariaLabel="Remove service"
                                    onClick={() => onRemoveRef.current(row)}
                                    startIcon={<Trash2Icon size={18} />}
                                    disabled={isRemoving || !!editingId}
                                    color="error"
                                />
                            </Tooltip>
                        </PermissionGuard>
                    </div>
                );
            },
        },
    ], [editingId, removingServiceId, navigate, onRemoveRef, backRefRef, startEdit]);

    if (isLoading) {
        return <LoadingSpinner size="lg" className="py-8" message="Loading services..." />;
    }

    return (
        <Table<PlanServiceRow>
            data={data}
            columns={columns}
            enableSorting={false}
            getRowId={(row) => row.id}
            expandedRowId={editingId}
            renderExpanded={(row) => (
                <ServiceForm
                    planId={planId}
                    idPrefix={`plan-service-edit-${row.id}`}
                    initialValues={row}
                    submitLabel="Save Changes"
                    pendingLabel="Saving..."
                    isLoading={isUpdating}
                    genericErrorMessage="Could not save the service. Try again."
                    onSubmit={async (values) => {
                        await onUpdate(row.id, values);
                        setEditingId(null);
                    }}
                    onCancel={cancelEdit}
                    autoFocusName
                />
            )}
        />
    );
};
